import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";

const TrendsCharts = ({ newHiresTrend = [], exitsTrend = [], year }) => {
  // 📊 Helper: Find peak, low, and total
  const getSummary = (data, key) => {
    if (!data || data.length === 0) return null;
    const total = data.reduce((sum, d) => sum + (d[key] || 0), 0);
    const max = data.reduce((prev, curr) =>
      curr[key] > prev[key] ? curr : prev
    );
    const min = data.reduce((prev, curr) =>
      curr[key] < prev[key] ? curr : prev
    );
    return { total, max, min };
  };

  const hiresSummary = getSummary(newHiresTrend, "newHires");
  const exitsSummary = getSummary(exitsTrend, "exits");

  const displayYear = year || new Date().getFullYear();

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* 🟩 New Hires Trend */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 430, display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              New Hires Trend (By Month) – {displayYear}
            </Typography>

            {newHiresTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={newHiresTrend}
                  margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="newHires"
                    stroke="#4caf50"
                    strokeWidth={3}
                    dot={{ r: 5 }}
                  >
                    <LabelList
                      dataKey="newHires"
                      position="top"
                      dy={ -7} 
                      formatter={(v) => v.toLocaleString()}
                      style={{
                        fontSize: 11,
                        fontWeight: "bold",
                        fill: "#4caf50",
                      }}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 5, textAlign: "center" }}
              >
                No New Hires data available
              </Typography>
            )}

            {hiresSummary && (
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#f5f5f5",
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid #eee",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  📈 Summary:
                </Typography>
                <Typography variant="caption" sx={{ color: "#555" }}>
                  Peak hiring in{" "}
                  <strong>{hiresSummary.max.monthName}</strong> (
                  {hiresSummary.max.newHires.toLocaleString()}), lowest in{" "}
                  <strong>{hiresSummary.min.monthName}</strong> (
                  {hiresSummary.min.newHires.toLocaleString()}).{" "}
                  Total hires in {displayYear}:{" "}
                  <strong>{hiresSummary.total.toLocaleString()}</strong>.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* 🟥 Exits Trend */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 430, display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              Exits Trend (By Month) – {displayYear}
            </Typography>

            {exitsTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={exitsTrend}
                  margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <Tooltip formatter={(v) => v.toLocaleString()} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="exits"
                    stroke="#f44336"
                    strokeWidth={3}
                    dot={{ r: 5 }}
                  >
                    <LabelList
                      dataKey="exits"
                      position="top"
                      dy={-7} 
                      formatter={(v) => v.toLocaleString()}
                      style={{
                        fontSize: 11,
                        fontWeight: "bold",
                        fill: "#f44336",
                      }}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 5, textAlign: "center" }}
              >
                No Exits data available
              </Typography>
            )}

            {exitsSummary && (
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#f5f5f5",
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid #eee",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  📉 Summary:
                </Typography>
                <Typography variant="caption" sx={{ color: "#555" }}>
                  Highest exits in{" "}
                  <strong>{exitsSummary.max.monthName}</strong> (
                  {exitsSummary.max.exits.toLocaleString()}), lowest in{" "}
                  <strong>{exitsSummary.min.monthName}</strong> (
                  {exitsSummary.min.exits.toLocaleString()}).{" "}
                  Total exits in {displayYear}:{" "}
                  <strong>{exitsSummary.total.toLocaleString()}</strong>.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default TrendsCharts;
